import React, { useEffect } from "react";
import { Modal } from "../../../../../../../components/core/Modal/Modal";
import {
  Button,
  ButtonVariants,
} from "../../../../../../../components/core/Button/Button";
import {
  CANCEL_LABEL,
  SUB_COMPETENCE_LABEL,
  CONFIRM_LABEL,
  EDIT_LEVEL_TECH_SKILL,
  LEVEL_LABEL,
  SCOPE_LABEL,
  CORE_COMPETENCE_LABEL,
  VERIFICATION_STATUS_LABEL,
  CATEGORY_LABEL,
} from "../../../../../../../constants/Dictionary";
import { ReactComponent as CheckIcon } from "../../../../../../../icons/check.svg";
import { ReactComponent as CancelIcon } from "../../../../../../../icons/close.svg";
import { Loader } from "../../../../../../../components/core/Loader/Loader";
import {
  CollaboratorDetails,
  CollaboratorsHistoryTechSkill,
  UpdateCollaboratorsHistoryTechSkillRequest,
} from "../../../../../../../types/api/collaborators.type";
import { FormField } from "../../../../../../../components/core/FormField/FormField";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TextBox } from "../../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import {
  EditModalContentTech,
  StyledEditCollabTech,
} from "./EditTechSkillCollaboratorModal.styled";
import {
  Select,
  SelectVariantEnum,
} from "../../../../../../../components/core/Select/Select";
import {
  VERIFICATION_STATUS_COLORS,
  VERIFICATION_STATUS_LABELS,
} from "../../../../../../../constants/AppConstants";
import { VerificationStatusButton } from "../../../../../../../components/VerificationStatusButton/VerificationStatusButton";
import { useAppSelector } from "../../../../../../../hooks/store.hook";
import { useHistoryTechSkillsUpdate } from "../../../../../../../hooks/professionalHistory.hook";

type FormData = {
  level: string;
};

interface EditTechSkillCollaboratorModalProps {
  record: CollaboratorsHistoryTechSkill;
  isOpen: boolean;
  closeModal: () => void;
  updateList: () => Promise<void>;
  collaborator: CollaboratorDetails;
}

export const EditTechSkillCollaboratorModal = ({
  record,
  isOpen,
  closeModal,
  updateList,
  collaborator,
}: EditTechSkillCollaboratorModalProps) => {
  const { orgSettings } = useAppSelector((state) => state.config);

  const profileId = collaborator.id;

  const { mutateAsync: updateHistoryTechSkill, isPending } =
    useHistoryTechSkillsUpdate(profileId);

  const label = VERIFICATION_STATUS_LABELS[record.verificationStatus];
  const color = VERIFICATION_STATUS_COLORS[record.verificationStatus];

  const schema = yup.object({
    level: yup.string().required(),
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    if (record) {
      const body: UpdateCollaboratorsHistoryTechSkillRequest = {
        level: data.level,
      };
      await updateHistoryTechSkill({ record, body });
    }
    updateList();
    closeModal();
  };

  useEffect(() => {
    if (isOpen && record) {
      reset({ level: record.level });
    } else {
      reset({
        level: "",
      });
    }
  }, [isOpen, record]);

  return (
    <Modal
      label={EDIT_LEVEL_TECH_SKILL}
      fullSize={false}
      isOpen={isOpen}
      rightButton={
        <Button
          icon={<CheckIcon />}
          label={CONFIRM_LABEL}
          form={"updateCollaboratorTechSkillForm"}
          disabled={isPending || !isDirty}
        />
      }
      leftButton={
        <Button
          variant={ButtonVariants.SECONDARY}
          icon={<CancelIcon />}
          label={CANCEL_LABEL}
          onClick={() => closeModal()}
        />
      }
    >
      <Loader loading={isPending}>
        <EditModalContentTech>
          <StyledEditCollabTech $numCol={2}>
            <TextBox label={CATEGORY_LABEL} text={record.competence ?? ""} />
            <TextBox label={SCOPE_LABEL} text={record.stream ?? ""} />
            <TextBox
              label={CORE_COMPETENCE_LABEL}
              text={record.software ?? ""}
            />
            <TextBox
              label={SUB_COMPETENCE_LABEL}
              text={record.component ?? ""}
            />
          </StyledEditCollabTech>
          <StyledEditCollabTech $numCol={2}>
            <div className="container-status">
              <VerificationStatusButton
                label={label}
                color={color}
                title={VERIFICATION_STATUS_LABEL}
              />
            </div>
          </StyledEditCollabTech>
          <form
            id="updateCollaboratorTechSkillForm"
            onSubmit={handleSubmit(onSubmit)}
          >
            <StyledEditCollabTech $numCol={2}>
              <FormField error={errors.level?.message}>
                <Controller
                  name="level"
                  control={control}
                  render={({ field }) => (
                    <Select
                      variant={SelectVariantEnum.DEFAULT}
                      label={LEVEL_LABEL}
                      options={orgSettings.techSkills.levels}
                      allowClear={false}
                      id="level-select"
                      {...field}
                    />
                  )}
                />
              </FormField>
            </StyledEditCollabTech>
          </form>
        </EditModalContentTech>
      </Loader>
    </Modal>
  );
};
